import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Heart } from "lucide-react";

export default function Donate() {
  const [donations, setDonations] = useState([]);
  const [chosen, setChosen] = useState("donate_25");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [charity, setCharity] = useState(null);
  const [giftAid, setGiftAid] = useState(false);
  const [causes, setCauses] = useState([]);
  const [params] = useSearchParams();
  const [causeId, setCauseId] = useState(params.get("cause") || "");

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setDonations(data.donations || []));
    api.get("/charity/status").then(({ data }) => setCharity(data)).catch(() => {});
    api.get("/causes").then(({ data }) => setCauses(data.causes || [])).catch(() => {});
  }, []);

  const giftAidAvailable = !!charity?.gift_aid_enabled && !!charity?.is_registered;
  const giftAidBoost = giftAid && giftAidAvailable;
  const selectedCause = causes.find((c) => c.id === causeId);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post("/payments/checkout", {
        package_id: chosen,
        package_type: "donation",
        origin_url,
        donor_name: donor.name || "Anonymous",
        donor_email: donor.email || undefined,
        gift_aid: giftAidBoost,
        cause_id: causeId || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const tiers = donations.length
    ? donations
    : [
        { id: "donate_10", name: "Supporter Donation £10", amount: 10 },
        { id: "donate_25", name: "Friend Donation £25", amount: 25 },
        { id: "donate_50", name: "Dhamma Patron £50", amount: 50 },
        { id: "donate_100", name: "Sangha Benefactor £100", amount: 100 },
      ];

  return (
    <>
      <section className="bg-navy text-pearl py-20" data-testid="donate-hero">
        <div className="section-container">
          <p className="eyebrow text-gold">Dāna · the practice of giving</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Support our mission.</h1>
          <p className="mt-6 text-pearl/80 max-w-2xl">
            Your generosity sustains our community programmes &mdash; Dhamma teaching,
            community outreach, educational scholarships, free public events across the UK,
            and the running costs of our <strong className="text-pearl">charitable digital
            platform</strong> that brings the Sangha to anyone who cannot reach us in person.
          </p>
          {!charity?.is_registered && (
            <div className="mt-6 inline-flex items-start gap-2 px-4 py-3 bg-pearl/10 border border-saffron/40 rounded-sm text-xs text-pearl/85 max-w-2xl" data-testid="donate-cio-notice">
              <span className="text-saffron font-semibold">Please note:</span>
              <span>
                Charity registration is in progress (CIO application in preparation
                with the Charity Commission for England and Wales). Donations are
                gratefully received as voluntary support and are not currently
                eligible for Gift Aid — we will publish guidance once the charity
                is formally registered.
              </span>
            </div>
          )}
          {charity?.is_registered && (
            <div className="mt-6 inline-flex items-start gap-2 px-4 py-3 bg-green-900/30 border border-green-400/40 rounded-sm text-xs text-pearl/90 max-w-2xl" data-testid="donate-registered-notice">
              <span className="text-green-300 font-semibold">Registered charity:</span>
              <span>
                Charity no. <strong>{charity.charity_number}</strong>
                {giftAidAvailable ? " · Gift Aid eligible — UK taxpayers can boost their donation by 25%" : ""}
              </span>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-pearl">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Choose an amount</p>
            <h2 className="mt-3 font-display text-4xl text-navy">Every pound is put to work.</h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {tiers.map((t) => {
                const active = chosen === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setChosen(t.id)}
                    data-testid={`donate-tier-${t.id}`}
                    className={`p-6 border rounded-sm text-center transition-all ${
                      active ? "border-gold bg-gold/5 ring-1 ring-gold" : "border-navy/15 bg-white hover:border-navy/30"
                    }`}
                  >
                    <div className="font-display text-3xl text-navy">£{t.amount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                      {t.id.replace("donate_", "").includes("100") ? "Benefactor" : "Give"}
                    </div>
                  </button>
                );
              })}
            </div>

            <blockquote className="mt-12 border-l-4 border-gold pl-6 font-display italic text-2xl text-navy">
              "Dāna is the first of the Buddha's ten pāramī: the perfection of generosity."
            </blockquote>

            {causes.length > 0 && (
              <div className="mt-10" data-testid="donate-cause-picker">
                <p className="eyebrow">Direct your gift (optional)</p>
                <h3 className="mt-2 font-display text-2xl text-navy">Choose a cause</h3>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCauseId("")}
                    data-testid="cause-pick-general"
                    className={`text-left px-4 py-3 border rounded-sm transition-colors ${
                      !causeId ? "border-navy bg-navy/5 ring-1 ring-navy" : "border-navy/15 bg-white hover:border-navy/30"
                    }`}
                  >
                    <div className="text-sm font-medium text-navy">General Sangha fund</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Trustees direct funds where most needed.</div>
                  </button>
                  {causes.map((c) => {
                    const active = causeId === c.id;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setCauseId(c.id)}
                        data-testid={`cause-pick-${c.id}`}
                        className={`text-left px-4 py-3 border rounded-sm transition-colors ${
                          active ? "ring-1 bg-pearl/40" : "border-navy/15 bg-white hover:border-navy/30"
                        }`}
                        style={active ? { borderColor: c.accent, "--tw-ring-color": c.accent } : {}}
                      >
                        <div className="text-sm font-medium text-navy">{c.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{c.tagline}</div>
                      </button>
                    );
                  })}
                </div>
                {selectedCause && (
                  <p className="mt-3 text-xs italic text-slate-500">
                    Your gift will be tagged "{selectedCause.name}" in our records and reported separately in our annual transparency review.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <form
              onSubmit={submit}
              className="bg-white border border-navy/10 p-8 sm:p-10 rounded-sm"
              data-testid="donate-form"
            >
              <p className="eyebrow">Your details (optional)</p>
              <h2 className="mt-3 font-display text-3xl text-navy">Proceed to payment</h2>
              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Name</span>
                  <input
                    value={donor.name}
                    onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                    placeholder="Anonymous if left blank"
                    data-testid="donate-name"
                    className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-slate-500">Email (optional)</span>
                  <input
                    type="email"
                    value={donor.email}
                    onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                    data-testid="donate-email"
                    className="mt-2 w-full border border-navy/15 px-4 py-3 rounded-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>
              {giftAidAvailable && (
                <label
                  className={`mt-5 flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                    giftAid ? "bg-green-50 border-green-300" : "bg-pearl border-navy/15"
                  }`}
                  data-testid="donate-gift-aid"
                >
                  <input
                    type="checkbox"
                    checked={giftAid}
                    onChange={(e) => setGiftAid(e.target.checked)}
                    className="mt-1"
                    data-testid="donate-gift-aid-checkbox"
                  />
                  <div className="text-xs text-slate-700 leading-relaxed">
                    <strong className="text-navy text-sm block mb-1">Boost your donation by 25% with Gift Aid</strong>
                    I confirm I am a UK taxpayer and want the {charity?.is_registered ? `Ambedkarite Buddhist Global Network UK (charity no. ${charity.charity_number})` : "Ambedkarite Buddhist Global Network UK"} to reclaim Gift Aid on this donation and any future donations I make until I notify them otherwise. I understand that I must pay an amount of UK Income Tax or Capital Gains Tax in the current tax year at least equal to the amount of Gift Aid claimed on my donations.
                  </div>
                </label>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full py-3.5 bg-gold text-navy hover:bg-gold-light rounded-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="donate-submit"
              >
                <Heart size={18} /> {submitting ? "Redirecting…" : `Donate £${tiers.find((t) => t.id === chosen)?.amount || ""}${giftAidBoost ? ` (+£${Math.round((tiers.find((t) => t.id === chosen)?.amount || 0) * 0.25 * 100) / 100} Gift Aid)` : ""}`}
              </button>
              <p className="mt-6 text-xs text-slate-500">
                Secure payment powered by Stripe. You'll be redirected to a secure checkout page.
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
